import type {BenchmarkConfidenceFilter, BenchmarkOrdering} from "../types/benchmarkTypes";
import type {SupplierOrdering, TransactionOrdering} from "../types/categoryProfileTypes";

/** URLs relative to `queryHttp`'s base (`<cat-material>/api`). Every route is scoped by databag. */

const base = (databagId: number) => `category_profile/${databagId}`;

/** A category's risk score with its explanation (the Explore tree carries only the level). */
export function riskScoreUrl(databagId: number, path: string): string {
    return `${base(databagId)}/risk/?${new URLSearchParams({path}).toString()}`;
}

/** The team's input on a category's risk (`acknowledge` keeps it after a refresh changed the calculation). */
export function riskOverrideUrl(databagId: number, path: string, sub?: 'acknowledge'): string {
    return `${base(databagId)}/risk/override/${sub ? `${sub}/` : ''}?${new URLSearchParams({path}).toString()}`;
}

/** Risk scoring preview for a dataset, with settings not saved yet. */
export function riskPreviewUrl(databagId: number): string {
    return `${base(databagId)}/risk/preview/`;
}

/** An Approval page request for a category plan: its databag, category and round. */
export function planApprovalUrl(approvalId: number | string): string {
    return `category_profile_approval/${approvalId}/`;
}

/** Explore over the Reporting period setting, or a fixed window of `months`. */
export function exploreUrl(databagId: number, months?: number): string {
    return `${base(databagId)}/explore/${months ? `?months=${months}` : ''}`;
}

function profileUrl(databagId: number, section: string, path: string, extra: Record<string, string> = {}): string {
    const qs = new URLSearchParams({path, ...extra});
    return `${base(databagId)}/profile/${section ? `${section}/` : ''}?${qs.toString()}`;
}

export function profileHeaderUrl(databagId: number, path: string, savingsYear?: number | 'all'): string {
    return profileUrl(databagId, '', path, savingsYear ? {savingsYear: String(savingsYear)} : {});
}

export function profileSpendUrl(databagId: number, path: string): string {
    return profileUrl(databagId, 'spend', path);
}

export type SuppliersParams = {
    path: string;
    ordering: SupplierOrdering;
    page: number;
    pageSize: number;
    search: string;
    country: string;
    /** Share of spend carried by the largest suppliers (100 = all). */
    cut: number;
    /** One row per supplier, or per normalized supplier (Supplier 360 entity / supplier normalization); `auto` is by
     * normalized name when the dataset has normalized names. */
    group?: 'auto' | 'supplier' | 'normalized';
};

export function profileSuppliersUrl(databagId: number, p: SuppliersParams): string {
    return profileUrl(databagId, 'suppliers', p.path, {
        ordering: p.ordering, offset: String(p.page * p.pageSize), limit: String(p.pageSize),
        search: p.search, country: p.country, cut: String(p.cut), group: p.group ?? 'auto',
    });
}

export function profileContractsUrl(databagId: number, path: string): string {
    return profileUrl(databagId, 'contracts', path);
}

export type TransactionsParams = {
    path: string;
    ordering: TransactionOrdering;
    page: number;
    pageSize: number;
    search: string;
};

export function profileTransactionsUrl(databagId: number, p: TransactionsParams): string {
    return profileUrl(databagId, 'transactions', p.path, {
        ordering: p.ordering,
        offset: String(p.page * p.pageSize),
        limit: String(p.pageSize),
        search: p.search,
    });
}

// --- Category record (authored data) ---

export type RecordCollection = 'team' | 'requirements' | 'notes' | 'risks';

export function recordUrl(databagId: number, path: string): string {
    return `${base(databagId)}/record/?${new URLSearchParams({path}).toString()}`;
}

/** Create in a collection of the record of `path`. */
export function recordCollectionUrl(databagId: number, collection: RecordCollection, path: string): string {
    return `${base(databagId)}/record/${collection}/?${new URLSearchParams({path}).toString()}`;
}

export function recordItemUrl(databagId: number, collection: RecordCollection, id: number): string {
    return `${base(databagId)}/record/${collection}/${id}/`;
}

export function recordRestoreUrl(databagId: number, collection: RecordCollection, id: number): string {
    return `${recordItemUrl(databagId, collection, id)}restore/`;
}

export function requirementAttachmentUrl(databagId: number, id: number): string {
    return `${recordItemUrl(databagId, 'requirements', id)}attachment/`;
}

export function noteReactionsUrl(databagId: number, id: number): string {
    return `${recordItemUrl(databagId, 'notes', id)}reactions/`;
}

// --- Category plan (Strategy tab) ---

export type PlanAction = '' | 'submit' | 'withdraw' | 'decide' | 'history';

export function planUrl(databagId: number, path: string, actionName: PlanAction = ''): string {
    return `${base(databagId)}/plan/${actionName ? `${actionName}/` : ''}?${new URLSearchParams({path}).toString()}`;
}

// --- Internal benchmark (Benchmarks tab) ---

export type BenchmarksParams = {
    path: string;
    confidence: BenchmarkConfidenceFilter;
    ordering: BenchmarkOrdering;
    page: number;
    pageSize: number;
};

export function benchmarksUrl(databagId: number, p: BenchmarksParams): string {
    return profileUrl(databagId, 'benchmarks', p.path, {
        confidence: p.confidence, ordering: p.ordering, offset: String(p.page * p.pageSize), limit: String(p.pageSize),
    });
}

export function benchmarkDetailUrl(databagId: number, path: string, id: number): string {
    return profileUrl(databagId, `benchmarks/${id}`, path);
}

// --- Market index ---

export function marketUrl(databagId: number, path: string): string {
    return profileUrl(databagId, 'market', path);
}

export function marketDriversUrl(databagId: number, path: string, mappingId?: number): string {
    return profileUrl(databagId, mappingId ? `market/drivers/${mappingId}` : 'market/drivers', path);
}

export function marketSeriesUrl(databagId: number): string {
    return `${base(databagId)}/market/series/`;
}

/** Category Profile settings (Mithra staff only); per deployment, not per databag. */
export const SETTINGS_URL = 'category_profile_settings/';
/** What customers see of the Category Profile (dashboard card, hidden tabs): readable by every signed-in user. */
export const DISPLAY_URL = `${SETTINGS_URL}display/`;

export function settingsUrl(section: 'values' | 'ingest' | `series/${number}` | `builds/${number}` | `signals/${number}`): string {
    return `${SETTINGS_URL}${section}/`;
}

/** The contract price monitor of a category, one clause, or a clause's restore (Undo). */
export function monitorUrl(databagId: number, path: string, clauseId?: number, restore = false): string {
    const item = clauseId ? `${clauseId}/${restore ? 'restore/' : ''}` : '';
    return profileUrl(databagId, `monitor/${item}`.replace(/\/$/, ''), path);
}

/** External signals of a category, and its external taxonomy mapping. */
export function signalsUrl(databagId: number, path: string, mapping = false): string {
    return profileUrl(databagId, mapping ? 'signals/mapping' : 'signals', path);
}

/** Should cost of a category: models, one model (`model/<id>`, `model/<id>/restore`, `model/<id>/quotes`) or a quote. */
export function shouldCostUrl(databagId: number, path: string, sub = ''): string {
    return profileUrl(databagId, sub ? `should-cost/${sub}` : 'should-cost', path);
}
